import { Painter, shade } from './pixel';
import { C } from './palette';

export const TILE = 16;

export type FloorKind =
  | 'wood' | 'woodPale' | 'carpetRed' | 'carpetBlue' | 'carpetGreen' | 'stone' | 'checker' | 'dirt' | 'brick' | 'plank' | 'marble' | 'grassTile';
export type WallStyle = 'green' | 'red' | 'blue' | 'cream' | 'panel' | 'plaster' | 'brick' | 'stone';

const floorCache = new Map<string, HTMLCanvasElement[]>();
const wallCache = new Map<string, HTMLCanvasElement>();

export function floorTile(kind: FloorKind, variant: number): HTMLCanvasElement {
  let arr = floorCache.get(kind);
  if (!arr) {
    arr = [makeFloor(kind, 0), makeFloor(kind, 1), makeFloor(kind, 2)];
    floorCache.set(kind, arr);
  }
  return arr[variant % arr.length];
}

function makeFloor(kind: FloorKind, v: number): HTMLCanvasElement {
  const p = new Painter(TILE, TILE);
  switch (kind) {
    case 'wood': case 'woodPale': case 'plank': {
      const base = kind === 'woodPale' ? C.woodPale : kind === 'plank' ? C.woodDk : C.wood;
      p.rect(0, 0, 16, 16, base);
      for (let y = 0; y < 16; y += 8) {
        p.rect(0, y, 16, 7, shade(base, ((v + y) % 3 - 1) * 0.045));
        p.hline(0, y + 7, 16, shade(base, -0.2));
        p.hline(0, y, 16, shade(base, 0.13));
        p.hline((v * 5 + y) % 9, y + 3, 6, shade(base, -0.075));
        p.hline((v * 3 + 2) % 8, y + 5, 8, shade(base, 0.065));
      }
      if (v === 2) { p.vline(11, 0, 7, shade(base, -0.22)); p.px(10, 2, shade(base, -0.3)); }
      if (kind === 'plank') p.hline(2, 12, 6, C.woodLine).px(4, 13, C.woodLine);
      break;
    }
    case 'carpetRed': case 'carpetBlue': case 'carpetGreen': {
      const base = kind === 'carpetRed' ? C.carpetRed : kind === 'carpetBlue' ? C.carpetBlue : C.carpetGreen;
      p.rect(0, 0, 16, 16, base);
      // Low-contrast woven texture stays behind the silhouettes of furniture.
      for (let y = 1; y < 16; y += 3) for (let x = (y % 2) + 1; x < 16; x += 4) p.px(x, y, shade(base, -0.065));
      const col = shade(base, 0.10);
      p.px(8, 4, col).px(7, 5, col).px(9, 5, col).px(6, 6, col).px(10, 6, col);
      p.px(7, 7, col).px(9, 7, col).px(8, 8, col);
      break;
    }
    case 'stone': {
      p.rect(0, 0, 16, 16, C.stone);
      p.hline(0, 7, 16, C.stoneLine).hline(0, 15, 16, C.stoneLine);
      p.vline(v === 1 ? 11 : 6, 0, 7, C.stoneLine).vline(v === 1 ? 3 : 13, 8, 7, C.stoneLine);
      p.noise(0, 0, 16, 16, C.stoneDk, 0.1, v + 2).noise(0, 0, 16, 16, C.stoneLt, 0.05, v + 30);
      break;
    }
    case 'marble': {
      p.rect(0, 0, 16, 16, C.tileA);
      p.hline(0, 15, 16, C.sheetSh).vline(15, 0, 16, C.sheetSh);
      p.hline(0, 0, 15, C.sheet).vline(0, 0, 15, C.sheet);
      const vein = shade(C.tileA, -0.065);
      for (let y = 2; y < 14; y++) {
        const x = (Math.floor(y * 0.55) + v * 5) % 14;
        p.px(x, y, vein);
        if (y % 4 === 0) p.px(x + 1, y, vein);
      }
      break;
    }
    case 'checker': {
      for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
        const base = (x + y) % 2 === 0 ? C.tileA : C.tileB;
        p.rect(x * 8, y * 8, 8, 8, base);
        p.hline(x * 8, y * 8, 8, shade(base, 0.12)).vline(x * 8 + 7, y * 8, 8, shade(base, -0.14));
      }
      break;
    }
    case 'dirt':
      p.rect(0, 0, 16, 16, C.dirt).noise(0, 0, 16, 16, C.dirtDk, 0.18, v + 1).noise(0, 0, 16, 16, C.dirtLt, 0.08, v + 8);
      break;
    case 'brick': {
      p.rect(0, 0, 16, 16, C.mortar);
      for (let y = 0; y < 16; y += 4) {
        const off = (y / 4) % 2 === 0 ? 0 : 4;
        for (let x = -4; x < 16; x += 8) p.rect(x + off, y, 7, 3, C.brick);
      }
      p.noise(0, 0, 16, 16, C.brickDk, 0.1, v).noise(0, 0, 16, 16, C.brickLt, 0.05, v + 3);
      break;
    }
    case 'grassTile':
      p.rect(0, 0, 16, 16, '#2f4a2a').noise(0, 0, 16, 16, '#3c5c34', 0.2, v).noise(0, 0, 16, 16, '#26391f', 0.1, v + 9);
      break;
  }
  return p.done();
}

/** Front face of a wall (drawn on wall tiles that have floor directly below them). */
export function wallFace(style: WallStyle): HTMLCanvasElement {
  let c = wallCache.get('face:' + style);
  if (c) return c;
  const p = new Painter(TILE, TILE);
  const paper = (base: string, dk: string, lt: string) => {
    p.rect(0, 0, 16, 16, base);
    p.vline(0, 3, 10, dk).vline(15, 3, 10, dk);
    for (const x of [4, 11]) {
      p.px(x, 5, lt).hline(x - 1, 6, 3, lt).px(x, 7, lt);
      p.px(x, 9, dk).hline(x - 1, 10, 3, dk).px(x, 11, dk);
    }
    p.rect(0, 0, 16, 3, lt).hline(0, 2, 16, dk); // cornice
    p.rect(0, 13, 16, 3, C.dado).hline(0, 13, 16, C.panelLt);
  };
  switch (style) {
    case 'green': paper(C.paperGreen, C.paperGreenDk, C.paperGreenLt); break;
    case 'red': paper(C.paperRed, C.paperRedDk, C.paperRedLt); break;
    case 'blue': paper(C.paperBlue, C.paperBlueDk, C.paperBlueLt); break;
    case 'cream': paper(C.paperCream, C.paperCreamDk, C.paperCreamLt); break;
    case 'panel':
      p.rect(0, 0, 16, 16, C.panel);
      p.box(1, 3, 6, 9, C.panelDk).box(9, 3, 6, 9, C.panelDk);
      p.hline(2, 4, 4, C.panelLt).hline(10, 4, 4, C.panelLt);
      p.rect(0, 0, 16, 2, C.panelLt).hline(0, 2, 16, C.panelDk);
      p.rect(0, 13, 16, 3, C.dado).hline(0, 13, 16, C.panelLt);
      break;
    case 'plaster':
      p.rect(0, 0, 16, 16, C.plaster).noise(0, 0, 16, 16, C.plasterDk, 0.1, 5).noise(0, 0, 16, 16, C.plasterLt, 0.05, 6);
      p.rect(0, 0, 16, 2, C.plasterLt);
      p.rect(0, 13, 16, 3, C.plasterDk);
      break;
    case 'brick':
      p.rect(0, 0, 16, 16, C.mortar);
      for (let y = 0; y < 16; y += 4) {
        const off = (y / 4) % 2 === 0 ? 0 : 4;
        for (let x = -4; x < 16; x += 8) p.rect(x + off, y, 7, 3, C.brick);
      }
      p.noise(0, 0, 16, 16, C.brickLt, 0.06, 2);
      break;
    case 'stone':
      p.rect(0, 0, 16, 16, C.stoneDk);
      p.rect(0, 0, 7, 7, C.stone).rect(8, 0, 8, 7, C.stone).rect(0, 8, 4, 8, C.stone).rect(5, 8, 11, 8, C.stone);
      p.noise(0, 0, 16, 16, C.stoneLt, 0.05, 1).noise(0, 0, 16, 16, C.stoneLine, 0.05, 4);
      break;
  }
  c = p.done();
  wallCache.set('face:' + style, c);
  return c;
}

export function wallTop(): HTMLCanvasElement {
  let c = wallCache.get('top');
  if (c) return c;
  const p = new Painter(TILE, TILE);
  p.rect(0, 0, 16, 16, C.wallTop).vline(0, 0, 16, C.wallTopLt);
  c = p.done();
  wallCache.set('top', c);
  return c;
}

/** Edge highlight drawn on top of a wall-top tile where it borders a wall face below (the "cap" line). */
export function wallCap(): HTMLCanvasElement {
  let c = wallCache.get('cap');
  if (c) return c;
  const p = new Painter(TILE, TILE);
  p.hline(0, 15, 16, '#3a3348');
  c = p.done();
  wallCache.set('cap', c);
  return c;
}

/** Soft shadow strip under north walls, for depth. */
export function floorShadow(): HTMLCanvasElement {
  let c = wallCache.get('shadow');
  if (c) return c;
  const p = new Painter(TILE, TILE);
  p.rect(0, 0, 16, 3, 'rgba(0,0,0,0.35)').rect(0, 3, 16, 2, 'rgba(0,0,0,0.18)');
  c = p.done();
  wallCache.set('shadow', c);
  return c;
}

export function stairsTile(dir: 'up' | 'down', row: number): HTMLCanvasElement {
  const key = `stairs:${dir}:${row}`;
  let c = wallCache.get(key);
  if (c) return c;
  const p = new Painter(TILE, TILE);
  p.rect(0, 0, 16, 16, C.woodDk);
  for (let y = 0; y < 16; y += 4) {
    p.hline(0, y, 16, C.woodLt);
    p.hline(0, y + 3, 16, C.woodLine);
  }
  p.vline(0, 0, 16, C.panelDk).vline(15, 0, 16, C.panelDk);
  if (dir === 'down') {
    // darker as it goes down
    p.rect(0, 0, 16, 16, `rgba(0,0,0,${0.15 + row * 0.15})`);
  }
  c = p.done();
  wallCache.set(key, c);
  return c;
}
